import type * as braintree from "braintree-web";
import { useState } from "react";

import type {
	PaymentNonce,
	SavedAch,
	SavedCard,
} from "~/shared/types/payment-types.ts";

import { usePostPayAccount } from "../../../hooks/use-post-pay-account.ts";
import { US_STATE_CODES } from "../../utils/constants.ts";
import type { BraintreeUSBankAccountPayload } from "./use-braintree.ts";

type NewPaymentValues = {
	billingAddress1: string;
	billingAddress2: string;
	city: string;
	state: string;
	zip: string;
	country: string;
	cardholderName: string;
	routingNumber: string;
	bankAccountNumber: string;
	accountHolderName: string;
	ownershipType: string;
	accountType: string;
	isDefault: boolean;
};

type UseSavePaymentMethodProps = {
	selectedMethod: "ACH" | "CreditCard" | null;
	newPaymentValues: NewPaymentValues;
	tokenizeCard: (options: {
		cardholderName: string;
	}) => Promise<braintree.HostedFieldsTokenizePayload>;
	tokenizeAch: (options: {
		routingNumber: string;
		accountNumber: string;
		accountHolderName: string;
		ownershipType: "personal" | "business";
		accountType: "checking" | "savings";
		billingAddress: {
			streetAddress: string;
			extendedAddress: string;
			locality: string;
			region: string;
			postalCode: string;
			countryCodeAlpha2: string;
		};
	}) => Promise<BraintreeUSBankAccountPayload>;
	handleApplyPaymentMethod: (
		paymentMethod: SavedCard | SavedAch | PaymentNonce | null,
	) => void;
	setShowForm: (show: boolean) => void;
	onClose: () => void;
};

type BillingAddress = {
	address1: string;
	address2: string;
	city: string;
	state: string;
	postalCode: string;
	countryCode: string;
};

const buildBillingAddress = (values: NewPaymentValues): BillingAddress => {
	const stateRaw = values.state.trim();
	const state =
		values.country === "USA"
			? US_STATE_CODES[stateRaw.toLowerCase()] || stateRaw.toUpperCase()
			: stateRaw;
	return {
		address1: values.billingAddress1.trim(),
		address2: values.billingAddress2.trim(),
		city: values.city.trim(),
		state,
		postalCode: values.zip.trim(),
		countryCode: "US",
	};
};

type TokenizedResult = {
	nonce: string;
	metadata: Partial<PaymentNonce>;
};

const tokenizeForSelectedMethod = async ({
	selectedMethod,
	newPaymentValues,
	billingAddress,
	tokenizeCard,
	tokenizeAch,
}: {
	selectedMethod: "ACH" | "CreditCard" | null;
	newPaymentValues: NewPaymentValues;
	billingAddress: BillingAddress;
	tokenizeCard: UseSavePaymentMethodProps["tokenizeCard"];
	tokenizeAch: UseSavePaymentMethodProps["tokenizeAch"];
}): Promise<TokenizedResult | null> => {
	if (selectedMethod === "CreditCard") {
		const cardholderName = newPaymentValues.cardholderName.trim();
		const payload = await tokenizeCard({ cardholderName });
		return {
			nonce: payload.nonce,
			metadata: {
				Last4: payload.details.lastFour,
				cardNumberLast4: payload.details.lastFour,
				cardType: payload.details.cardType,
				cardholderName,
			},
		};
	}
	if (selectedMethod === "ACH") {
		const accountHolderName = newPaymentValues.accountHolderName.trim();
		const routingNumber = newPaymentValues.routingNumber.trim();
		const bankAccountNumber = newPaymentValues.bankAccountNumber.trim();
		const payload = await tokenizeAch({
			routingNumber,
			accountNumber: bankAccountNumber,
			accountHolderName,
			ownershipType: newPaymentValues.ownershipType as "personal" | "business",
			accountType: newPaymentValues.accountType as "checking" | "savings",
			billingAddress: {
				streetAddress: billingAddress.address1,
				extendedAddress: billingAddress.address2,
				locality: billingAddress.city,
				region: billingAddress.state,
				postalCode: billingAddress.postalCode,
				countryCodeAlpha2: billingAddress.countryCode,
			},
		});
		return {
			nonce: payload.nonce,
			metadata: {
				Last4: bankAccountNumber.slice(-4),
				accountNumberLast4: bankAccountNumber.slice(-4),
				bankName: payload.details.bankName,
				routingNumber,
				accountHolderName,
				accountType: newPaymentValues.accountType,
			},
		};
	}
	return null;
};

export function useSavePaymentMethod({
	selectedMethod,
	newPaymentValues,
	tokenizeCard,
	tokenizeAch,
	handleApplyPaymentMethod,
	setShowForm,
	onClose,
}: UseSavePaymentMethodProps) {
	const [brainTreeError, setBrainTreeError] = useState<string | null>(null);
	const [isTokenizing, setIsTokenizing] = useState(false);
	const {
		savePaymentMethodAsync,
		isSaving: isSavingApi,
		error: savePaymentMethodError,
		resetError: resetSavePaymentMethodError,
	} = usePostPayAccount();

	const handleSavePaymentMethod = async () => {
		if (isTokenizing || isSavingApi) {
			return;
		}
		setIsTokenizing(true);
		try {
			const billingAddress = buildBillingAddress(newPaymentValues);
			const tokenized = await tokenizeForSelectedMethod({
				selectedMethod,
				newPaymentValues,
				billingAddress,
				tokenizeCard,
				tokenizeAch,
			});

			if (tokenized) {
				const { nonce, metadata } = tokenized;
				const response = await savePaymentMethodAsync({
					paymentMethodNonce: nonce,
					makeDefault: newPaymentValues.isDefault,
					paymentMethod: selectedMethod === "ACH" ? 1 : 2,
					billingAddress,
					cardholderName:
						selectedMethod === "CreditCard"
							? newPaymentValues.cardholderName.trim()
							: undefined,
					accountHolderName:
						selectedMethod === "ACH"
							? newPaymentValues.accountHolderName.trim()
							: undefined,
				});

				const accountResponse = response as SavedCard | SavedAch;

				handleApplyPaymentMethod({
					nonce,
					paymentMethodToken: accountResponse.paymentMethodToken,
					paymentMethod: selectedMethod === "ACH" ? "ACH" : "CreditCard",
					isDefault: accountResponse.isDefault,
					...metadata,
				});
			}

			setShowForm(false);
			onClose();
		} catch (error) {
			console.error("Failed to save payment method:", error);
			setBrainTreeError(
				error instanceof Error
					? error.message
					: "An unexpected error occurred while saving the payment method.",
			);
		} finally {
			setIsTokenizing(false);
		}
	};

	const resetErrors = () => {
		setBrainTreeError(null);
		resetSavePaymentMethodError();
	};

	return {
		handleSavePaymentMethod,
		isSaving: isTokenizing || isSavingApi,
		brainTreeError,
		savePaymentMethodError,
		resetErrors,
	};
}
